import React, { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import mapboxgl from "mapbox-gl";
import { Provider, useSelector } from "react-redux";
import t from "../common/localization";
import { useAttributePreference } from "../common/preferences";

import { map } from "./Map";
import store from "../store";
import { useHistory } from "react-router-dom";
import { Grid, Paper, List, ListItem, makeStyles } from "@material-ui/core";
import Avatar from "@material-ui/core/Avatar";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import {
  formatDistance,
  formatHours,
  formatDate,
  formatSpeed,
  formatVolume,
  formatPosition,
  formatDurationTime,
} from "../common/formatter";
import DriveEtaIcon from "@material-ui/icons/DriveEta";
import SpeedIcon from "@material-ui/icons/Speed";
import Chip from "@material-ui/core/Chip";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import FaceIcon from "@material-ui/icons/Face";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";

function mapStateToProps(state) {
  return { positions: state.positions.items };
}

const getIconBackgroundColor = (position) => {
  if (!position || !position.id) {
    return "black";
  }

  if (position.attributes && position.attributes.motion) {
    return "#00C49F";
  }

  if (position.attributes && position.attributes.ignition) {
    return "#FFBB28";
  }

  return "#E82C38";
};

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    borderRadius: 15,
    // backgroundColor: "#F1F3F6",
    fontSize: 12,
    width: "300px",
  },

  list: {
    width: "100%",
    backgroundColor: theme.palette.background.paper,
  },
  listItem: {
    paddingLeft: "15px",
    paddingRight: "1px",
  },
  value: {
    margin: "0",
    textAlign: "center",
    fontSize: "12px",
    color: "#1F1F51",
  },
  colP: {
    marginLeft: "-44px",
    margin: "0",
    textAlign: "left",
    fontSize: "12px",
    paddingLeft: "27px",
  },
  fabButtonsSecond: {
    maxHeight: "24px",
    minHeight: "24px",
    minWidth: "24px",
    maxWidth: "24px",
    fontSize: "1rem",
    backgroundColor: "#3F51B2",
  },
  fabButtonsFace: {
    maxHeight: "24px",
    minHeight: "24px",
    minWidth: "24px",
    maxWidth: "24px",
    fontSize: "1rem",
    backgroundColor: "#FF7D17",
    marginBottom: "10px",
  },
  fabButtonsSpeed: {
    maxHeight: "24px",
    minHeight: "24px",
    minWidth: "24px",
    maxWidth: "24px",
    fontSize: "1rem",
    backgroundColor: "#179912",
    marginBottom: "10px",
  },
  fabButtonsRunning: {
    maxHeight: "24px",
    minHeight: "24px",
    minWidth: "24px",
    maxWidth: "24px",
    fontSize: "1rem",
    backgroundColor: "#179912",
    marginBottom: "10px",
  },
  fabButtonsStatus: {
    maxHeight: "24px",
    minHeight: "24px",
    minWidth: "24px",
    maxWidth: "24px",
    fontSize: "1rem",
    backgroundColor: "#3F51B2",
    marginBottom: "10px",
  },
  chip: {
    margin: theme.spacing(0.1),
  },
}));

const getIconForMarker = (position, category) => {
  const topView = true;
  const availableImageList = [];
  availableImageList.push("car");
  availableImageList.push("truck");
  availableImageList.push("motorcycle");
  availableImageList.push("tractor");
  availableImageList.push("bus");
  availableImageList.push("pickup");
  availableImageList.push("van");
  availableImageList.push("ambulance");
  availableImageList.push("three_wheeler");
  availableImageList.push("e_rickshaw");
  availableImageList.push("jcb");
  availableImageList.push("crane");
  availableImageList.push("mixer_truck");
  availableImageList.push("person");
  availableImageList.push("arrow");
  availableImageList.push("bicycle");
  availableImageList.push("animal");
  availableImageList.push("boat");
  availableImageList.push("ship");

  let categoryName = category || "car";
  if (!availableImageList.some((attribute) => attribute === category)) {
    categoryName = "car";
  }
  categoryName = "category_" + (topView ? "top_" : "") + categoryName;

  if (position.attributes && position.attributes.motion) {
    return categoryName + "_running";
  }
  if (position.attributes && position.attributes.ignition) {
    return categoryName + "_idle";
  }
  return categoryName + "_stopped";
};
const markers = [];
const PositionsMap = ({ positions }) => {
  const id = "positions";
  const history = useHistory();
  const [zoomCalled, setZoomCalled] = useState(false);
  // const devices = useSelector(state => state.devices.items);

  const createFeature = (position) => {
    // const device = devices[position.deviceId] || null;
    const speed = formatSpeed(position.speed ? position.speed : 0, "kmh");
    var sinceStatus = "idleSince";
    var sinceTime = position.attributes.idleSince;
    if (position.attributes.currentStatus === "RUNNING") {
      sinceStatus = "runningSince";
      sinceTime = position.attributes.runningSince;
    } else if (position.attributes.currentStatus === "STOPPED") {
      sinceStatus = "stoppedSince";
      sinceTime = position.attributes.stoppedSince;
    }
    return {
      deviceId: position.deviceId,
      name: position ? position.name : "",
      category: getIconForMarker(position, position.category),
      course: position.course || 0,
      position: position,
      speed: speed,
      sinceStatus: sinceStatus,
      sinceTime: sinceTime,
      driverName: position.driverName || "Not set",
      currentStatus: position.currentStatus,
      address: position.address || "",
      currentStatus: position.attributes.currentStatus,
    };
  };

  const onMouseEnter = () => (map.getCanvas().style.cursor = "pointer");
  const onMouseLeave = () => (map.getCanvas().style.cursor = "");
  const classes = useStyles();

  const onClickCallback = useCallback(
    (event) => {
      const feature = event.features[0];
      let coordinates = feature.geometry.coordinates.slice();
      while (Math.abs(event.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += event.lngLat.lng > coordinates[0] ? 360 : -360;
      }
      const placeholder = document.createElement("div");

      ReactDOM.render(
        <div className={classes.root}>
          <Grid
            container
            spacing={0}
            justify="space-around"
            alignItems="center"
          >
            <Grid item xs={7}>
              <ListItemAvatar
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingTop: "5px",
                }}
              >
                <Avatar className={classes.fabButtonsSecond}>
                  <DriveEtaIcon style={{ width: "16px", height: "16px" }} />
                </Avatar>
                <b style={{ marginLeft: "5px", color: "#394B8A" }}>
                  {feature.properties.name}
                </b>
              </ListItemAvatar>
            </Grid>
            <Grid item xs={5}>
              <Chip
                label=<b>
                  {formatDate(
                    feature.properties.position.serverTime,
                    "MMM D, hh:mm A"
                  )}
                </b>
                icon={<AccessTimeIcon />}
                className={classes.chip}
                size="small"
                color="secondary"
                style={{
                  backgroundColor: "#E7F4DE",
                  color: "green",
                  borderRadius: 3,
                  marginLeft: "-20px",
                  marginTop: "10px",
                }}
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={1}
            direction="row"
            justify="space-evenly"
            alignItems="center"
          >
            <Grid item xs={1}>
              {
                <LocationOnIcon
                  style={{
                    color: "#0C9F95",
                    fontSize: "18",
                    alignItems: "right",
                  }}
                />
              }
            </Grid>
            <Grid item xs={11}>
              <marquee>
                {" "}
                <b>{feature.properties.address || "No Data"}</b>
              </marquee>
            </Grid>
          </Grid>
          <List className={classes.list}>
            <ListItem className={classes.listItem}>
              <ListItemAvatar
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingTop: "5px",
                }}
              >
                <Avatar className={classes.fabButtonsFace}>
                  <FaceIcon style={{ width: "16px", height: "16px" }} />
                </Avatar>
              </ListItemAvatar>
              <Grid item xs={6}>
                <b style={{ marginLeft: "-16px", color: "#394B8A" }}>
                  {feature.properties.driverName}
                </b>
                <p className={classes.colP}>{t("driverName")}</p>
              </Grid>
              <ListItemAvatar
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingTop: "5px",
                }}
              >
                <Avatar className={classes.fabButtonsSpeed}>
                  <SpeedIcon style={{ width: "16px", height: "16px" }} />
                </Avatar>
              </ListItemAvatar>
              <Grid item xs={6}>
                <b style={{ marginLeft: "-15px", color: "#394B8A" }}>
                  {feature.properties.speed}
                </b>
                <p className={classes.colP}>{t("positionSpeed")}</p>
              </Grid>
            </ListItem>
          </List>
          <List className={classes.list}>
            <ListItem className={classes.listItem}>
              <ListItemAvatar
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingTop: "5px",
                }}
              >
                <Avatar className={classes.fabButtonsRunning}>
                  <DriveEtaIcon style={{ width: "16px", height: "16px" }} />
                </Avatar>
              </ListItemAvatar>
              <Grid item xs={6}>
                <b style={{ marginLeft: "-15px", color: "#394B8A" }}>
                  {formatDurationTime(feature.properties.sinceTime)}
                </b>
                <p className={classes.colP}>
                  {t(feature.properties.sinceStatus)}
                </p>
              </Grid>
              <ListItemAvatar
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingTop: "5px",
                }}
              >
                <Avatar className={classes.fabButtonsStatus}>
                  <CheckCircleIcon style={{ width: "16px", height: "16px" }} />
                </Avatar>
              </ListItemAvatar>
              <Grid item xs={6}>
                <b style={{ marginLeft: "-15px", color: "#394B8A" }}>
                  {feature.properties.currentStatus}
                </b>
                <p className={classes.colP}>{t("currentStatus")}</p>
              </Grid>
            </ListItem>
          </List>
        </div>,

        // <Provider store={store}>
        // <StatusView deviceId={feature.properties.deviceId} onShowDetails={positionId => history.push(`/position/${positionId}`)} />
        // </Provider>,
        placeholder
      );

      new mapboxgl.Popup({
        offset: 25,
        anchor: "top",
      })
        .setDOMContent(placeholder)
        .setLngLat(coordinates)
        .addTo(map);
    },
    [history]
  );

  useEffect(() => {
    map.addSource(id, {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: [],
      },
    });
    map.addLayer({
      id: id,
      type: "symbol",
      source: id,
      layout: {
        "icon-image": "{category}",
        "icon-allow-overlap": true,
        "icon-size": 2,
        // 'icon-rotate': '{course}',
        "icon-rotate": ["get", "course"],
        // 'icon-rotation-alignment': 'map',
        "text-field": "{name}",
        "text-allow-overlap": true,
        "text-anchor": "bottom",
        "text-offset": [0, -1],
        "text-font": ["Roboto Regular"],
        "text-size": 14,
      },
      paint: {
        "text-halo-color": "white",
        "text-halo-width": 3,
      },
    });

    map.on("mouseenter", id, onMouseEnter);
    map.on("mouseleave", id, onMouseLeave);
    map.on("click", id, onClickCallback);

    return () => {
      Array.from(
        map.getContainer().getElementsByClassName("mapboxgl-popup")
      ).forEach((el) => el.remove());

      map.off("mouseenter", id, onMouseEnter);
      map.off("mouseleave", id, onMouseLeave);
      map.off("click", id, onClickCallback);

      map.removeLayer(id);
      map.removeSource(id);
    };
  }, [onClickCallback]);

  useEffect(() => {
    map.getSource(id).setData({
      type: "FeatureCollection",
      features: positions.map((position) => ({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [position.longitude, position.latitude],
        },
        properties: createFeature(position),
      })),
    });

    // positions && positions.map((position, index)=> {
    //   if(markers[index]){
    //     markers[index].remove();
    //   }
    //   const title = "Bantu jj"
    //   const icon = getIconForMarker(position, position.category);
    //   const course = position.course || 0;
    //   var el = document.createElement('div');
    //   el.className = 'marker';
    //   el.style.backgroundImage = "url(" + `images/icon/car.svg` + ")";
    //   el.style.width = '40px';
    //   el.style.height = '40px';
    //   markers[index] = new mapboxgl.Marker(el)
    //   .setLngLat([position.longitude, position.latitude]).setRotation(course).setPopup(new mapboxgl.Popup({ offset: 25 }) // add popups
    //   .setHTML('<h3>' + title + '</h3><p>' + ":hjndhj" + '</p>')).addTo(map);
    // })
    
    if (positions.length > 0 && !zoomCalled) {
      if (positions.length === 1) {
        const longitude = (positions[0] && positions[0].longitude) || 77;
        const latitude = (positions[0] && positions[0].latitude) || 28;

        map.setZoom("15");
        map.setCenter([longitude, latitude]);
        setZoomCalled(true);
      } else {
        // Fit bounds for multiple positions
        const bounds = new mapboxgl.LngLatBounds();
        positions.forEach(pos => bounds.extend([pos.longitude, pos.latitude]));
        map.fitBounds(bounds, { padding: 50 });
        setZoomCalled(true);
      }
    }

    // const longitude = (positions[0] && positions[0].longitude) || 77;
    // const latitude = (positions[0] && positions[0].latitude) || 28;

    // if (positions[0] && !zoomCalled) {
    //   map.setZoom("15");
    //   map.setCenter([longitude, latitude]);
    //   setZoomCalled(true);
    // }
  }, [positions]);

  return null;
};

export default PositionsMap;
